import { Button, ButtonProps } from "@mantine/core";
import { useTheme } from "@contexts/ThemeContext";
import { ReactNode } from "react";

export interface JarvisSecondaryButtonProps extends Omit<ButtonProps, 'leftSection' | 'rightSection' | 'onClick' | 'component' | 'type'> {
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  onClick?: () => void | Promise<void>;
  component?: any;
  type?: 'button' | 'submit' | 'reset';
}

/**
 * JarvisSecondaryButton - A themed secondary Button component that automatically applies
 * the current theme colors to match the application's design system.
 *
 * Usage: Use this for secondary actions (cancel, back, close, etc.)
 *
 * Props:
 * - leftIcon: ReactNode - Icon to display on the left side of the button
 * - rightIcon: ReactNode - Icon to display on the right side of the button
 * - All other Mantine Button props are supported
 */
export const JarvisSecondaryButton = ({ leftIcon, rightIcon, type, ...props }: JarvisSecondaryButtonProps) => {
  const { colors: themeColors } = useTheme();

  return (
    <Button
      {...props}
      type={type}
      variant="outline"
      leftSection={leftIcon}
      rightSection={rightIcon}
      styles={{
        root: {
          borderColor: themeColors.border,
          color: themeColors.text,
          backgroundColor: 'transparent',
          borderRadius: '10px',
          '&:hover': {
            backgroundColor: themeColors.background,
            borderColor: themeColors.border,
          },
          '&:disabled': {
            borderColor: themeColors.border,
            color: themeColors.textSecondary,
            backgroundColor: 'transparent',
          }
        },
        section: {
          color: themeColors.text,
        }
      }}
    />
  );
};

export default JarvisSecondaryButton;
